/**
 * Health Integration Constants
 *
 * Provider IDs and activity type mappings for Apple Health / Health Connect sync.
 * These MUST match the original Marune app exactly for backwards compatibility.
 *
 * @see /docs/APPLE_HEALTH_INTEGRATION_PLAN.md
 */

// Re-export provider IDs from central types
export { HEALTH_PROVIDER_IDS } from '../../types/health';

/**
 * AsyncStorage key for storing enabled providers
 * Matches original Marune app exactly
 */
export const STORAGE_KEY = 'marune.healthSyncService.enabledProviders';

/**
 * Activity type mapping: Marune training style → HealthKit activity type
 *
 * HealthKit activity types are strings that map to HKWorkoutActivityType enum values.
 * @see https://developer.apple.com/documentation/healthkit/hkworkoutactivitytype
 */
export const TRAINING_STYLE_TO_HEALTHKIT: Record<string, string> = {
  // Exact martial art matches
  'Boxing': 'boxing',
  'Kickboxing': 'kickboxing',
  'Muay Thai': 'kickboxing',
  'Wrestling': 'wrestling',
  'Freestyle Wrestling': 'wrestling',
  'Greco-Roman Wrestling': 'wrestling',

  // Generic martial arts
  'Brazilian Jiu Jitsu': 'martialArts',
  'Brazilian Jiu-Jitsu': 'martialArts',
  'BJJ': 'martialArts',
  'Judo': 'martialArts',
  'Karate': 'martialArts',
  'Taekwondo': 'martialArts',
  'Aikido': 'martialArts',
  'Krav Maga': 'martialArts',
  'Kung Fu': 'martialArts',
  'Capoeira': 'martialArts',
  'Sambo': 'martialArts',

  // MMA = Mixed Cardio (closest match)
  'MMA': 'mixedCardio',
  'Mixed Martial Arts': 'mixedCardio',

  // Default
  'Other': 'martialArts',
} as const;

/**
 * Activity type mapping: Marune exercise type → HealthKit activity type
 */
export const EXERCISE_TYPE_TO_HEALTHKIT: Record<string, string> = {
  'hiit': 'highIntensityIntervalTraining',
  'yoga': 'yoga',
  'resistance': 'functionalStrengthTraining',
  'running': 'running',
  'swimming': 'swimming',
  'cycling': 'cycling',
  'other': 'other',
} as const;

/**
 * HealthKit activity ID → Marune training style
 *
 * Used when IMPORTING workouts from Apple Health into Marune.
 * Maps HKWorkoutActivityType raw values to Marune trainedStyle strings.
 */
export const HEALTHKIT_ID_TO_TRAINING_STYLE: Record<number, string> = {
  16: 'Boxing',          // HKWorkoutActivityType.boxing
  17: 'Kickboxing',      // HKWorkoutActivityType.kickboxing
  47: 'Brazilian Jiu Jitsu', // HKWorkoutActivityType.martialArts (default)
  80: 'Wrestling',       // HKWorkoutActivityType.wrestling
  26: 'MMA',             // HKWorkoutActivityType.mixedCardio
} as const;

/**
 * HealthKit activity ID → Marune exercise type
 *
 * Used when IMPORTING workouts from Apple Health into Marune.
 */
export const HEALTHKIT_ID_TO_EXERCISE_TYPE: Record<number, string> = {
  37: 'running',         // HKWorkoutActivityType.running
  13: 'cycling',         // HKWorkoutActivityType.cycling
  46: 'swimming',        // HKWorkoutActivityType.swimming
  63: 'hiit',            // HKWorkoutActivityType.highIntensityIntervalTraining
  50: 'yoga',            // HKWorkoutActivityType.yoga
  20: 'resistance',      // HKWorkoutActivityType.functionalStrengthTraining
  35: 'resistance',      // HKWorkoutActivityType.coreTraining
} as const;

/**
 * HealthKit activity IDs that should be imported as TRAININGS (martial arts)
 * Other activity types will be imported as EXERCISES
 */
export const MARTIAL_ARTS_ACTIVITY_IDS = [16, 17, 47, 80, 26] as const;

/**
 * Get HealthKit activity type string for a training style
 */
export function getHealthKitActivityType(trainedStyle?: string): string {
  if (!trainedStyle) return 'martialArts';
  return TRAINING_STYLE_TO_HEALTHKIT[trainedStyle] || 'martialArts';
}

/**
 * Get HealthKit activity type string for an exercise type
 */
export function getHealthKitExerciseType(exerciseType?: string): string {
  if (!exerciseType) return 'other';
  return EXERCISE_TYPE_TO_HEALTHKIT[exerciseType] || 'other';
}

/**
 * Determine if a HealthKit workout should become a Training (vs Exercise)
 */
export function shouldBeTraining(workoutActivityId: number): boolean {
  return MARTIAL_ARTS_ACTIVITY_IDS.includes(workoutActivityId as any);
}

/**
 * Get Marune training style from HealthKit activity ID
 */
export function getTrainingStyleFromHealthKit(activityId: number): string {
  return HEALTHKIT_ID_TO_TRAINING_STYLE[activityId] || 'Brazilian Jiu Jitsu';
}

/**
 * Get Marune exercise type from HealthKit activity ID
 */
export function getExerciseTypeFromHealthKit(activityId: number): string {
  return HEALTHKIT_ID_TO_EXERCISE_TYPE[activityId] || 'other';
}
